/**
 * Shared chrome for every `/admin/*` screen (project brief §18/§30): a
 * section tab strip so the admin area reads as one place with nine rooms,
 * not eight unrelated pages. The "Admin" nav entry stays a single link
 * (`features/admin/index.ts#adminNavItems`) — this is what a click on it,
 * or on any `/admin/...` URL, actually lands the operator in.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, type TabItem } from '../../ui/Tabs/Tabs';
import { ADMIN_SECTIONS } from './sections';
import styles from './AdminLayout.module.css';

const SECTIONS = ADMIN_SECTIONS;

export interface AdminLayoutProps {
  /** Which section's tab is active — one of `SECTIONS[].value`. */
  section: string;
  children: ReactNode;
}

export function AdminLayout({ section, children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const items: TabItem[] = SECTIONS.map((s) => ({ value: s.value, label: s.label }));

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1>Admin</h1>
        <p className={styles.subtitle}>System configuration — organisation-wide, audited, and restricted to system admins.</p>
      </div>
      <Tabs
        aria-label="Admin sections"
        items={items}
        value={section}
        onChange={(value) => {
          const target = SECTIONS.find((s) => s.value === value);
          if (target) navigate(target.path);
        }}
      />
      <div className={styles.panel} role="tabpanel">
        {children}
      </div>
    </div>
  );
}
